import type { Role } from '@/features/auth/api';

/**
 * 操作マニュアルに載せる模式図（D-246 / D-254）。
 * 文章だけでは「どこに効くか」「次に何をするか」が想像できないため、
 * 箱と矢印で同じ内容を繰り返す。主要トピックには必ず1つ以上載せる。
 */
export type HelpDiagram = {
  title: string;
  lead?: string;
  roles?: Role[];
} & (
  | {
      kind: 'flow';
      /** 左から右へ。箱の中は「名前」と「一言」 */
      steps: { title: string; body: string }[];
    }
  | {
      kind: 'map';
      rows: { master: string; appearsIn: string }[];
    }
  | {
      kind: 'example';
      rows: { label: string; value: string }[];
      after?: string;
    }
);

const OFFICE: Role[] = ['office'];
const STUDENT: Role[] = ['student'];
const DIRECTOR: Role[] = ['director'];
const SUCCESS: Role[] = ['success'];

export const HELP_DIAGRAMS: Record<string, HelpDiagram[]> = {
  dashboard: [
    {
      kind: 'flow',
      title: '模式図: ホームの見かた',
      lead: 'いちばん上から順に見ます。赤い丸は「未読」だけで、作業の残り件数ではありません。',
      steps: [
        {
          title: '1. いまやること',
          body: '出欠・課題・問い合わせなど、いま手を付ける行が並ぶ。青い文字をクリック',
        },
        {
          title: '2. 直近のイベント',
          body: '自分に関係する近い回だけ。名前をクリックすると詳細へ',
        },
        {
          title: '3. メッセージ・通知',
          body: '新しいやり取りや、右上ベルの未読。開くと赤い丸が減る',
        },
      ],
    },
  ],

  members: [
    {
      kind: 'flow',
      roles: OFFICE,
      title: '模式図: 受講生を迎えるまで',
      lead: '名簿だけ作ってもログインできません。招待メールを受け取った本人が設定を終えて初めて入れます。',
      steps: [
        {
          title: '1. 期・コース',
          body: '業務マスターで選択肢を用意する（両方必須）',
        },
        {
          title: '2. 招待する',
          body: '会員一覧の右上から1人ずつ、または会員CSVでまとめて',
        },
        {
          title: '3. 本人が受諾',
          body: 'メールのリンクからパスワードなどを設定してログイン',
        },
        {
          title: '4. あとから直す',
          body: 'プロフィール編集／連携設定／参加イベント一覧の3系統',
        },
      ],
    },
    {
      kind: 'map',
      roles: OFFICE,
      title: '模式図: 会員まわりの編集は3つに分かれる',
      lead: '「編集」が1つだとステータス変更と勘違いしやすいので、入口を分けています。',
      rows: [
        {
          master: 'プロフィールを編集',
          appearsIn: '氏名・顔写真・会社・在籍状態など',
        },
        {
          master: '連携設定を編集',
          appearsIn: 'Meet用メール・Slackなど・ログイン情報の復旧',
        },
        {
          master: '参加イベント一覧',
          appearsIn: 'その受講生が対象になった回の一覧（結果画面へ）',
        },
      ],
    },
    {
      kind: 'flow',
      roles: ['director', 'success'],
      title: '模式図: 受講生の情報を見る',
      steps: [
        { title: '1. 一覧', body: '左メニュー「関わった会員」を開く' },
        { title: '2. 詳細', body: '「詳細」は公開プロフィールだけ（提出物は出ない）' },
        {
          title: '3. ポートフォリオ',
          body: '提出物・出欠・回答は行の「年間ポートフォリオ」から',
        },
        {
          title: '4. 連絡',
          body: '電話などは出ない。必要な連絡は事務局へ頼む',
        },
      ],
    },
    {
      kind: 'flow',
      roles: STUDENT,
      title: '模式図: ほかの受講生と自分',
      steps: [
        { title: '1. 受講生一覧', body: '公開されているプロフィールを見る' },
        { title: '2. マイプロフィール', body: '自分の顔写真・会社名などを直す' },
        {
          title: '3. 見えないもの',
          body: '電話番号などはほかの受講生には出ない',
        },
      ],
    },
  ],

  events: [
    {
      kind: 'flow',
      roles: OFFICE,
      title: '模式図: イベントを公開するまで',
      lead: '登録しただけでは受講生には見えません。右側の「公開する」まで進めて初めて見えます。',
      steps: [
        { title: '1. 種類を選ぶ', body: '左メニュー「イベント管理」の下から月例会などを開く' },
        {
          title: '2. 新規登録',
          body: '「月例会の新規登録」など種類名のボタン。この回のメールが見積もりで出る',
        },
        { title: '3. 対象・担当', body: '「対象者」「担当者」タブで誰が関わるかを決める' },
        {
          title: '4. 課題など',
          body: '必要なら課題・アンケートのタブで割り当てる（名前は種類ごとに違う）',
        },
        { title: '5. 公開する', body: '右側の「公開する」→確認でもう一度。対象の受講生に見える' },
      ],
    },
    {
      kind: 'map',
      roles: OFFICE,
      title: '模式図: イベント詳細の主なタブ',
      lead: '面談（サクセス面談・1on1）では出欠・課題などは使いません。1on1の事後アンケートだけは例外で使えます。席次のタブはありません。',
      rows: [
        { master: '概要', appearsIn: 'タイトル・日時・会場・定員。下書き中はここで編集' },
        { master: '対象者／担当者', appearsIn: '誰が参加するか／誰が担当するか' },
        {
          master: '課題・アンケートの4タブ',
          appearsIn:
            '名前は種類ごとに違う。月例会＝事前課題／当日アウトプット／事後アンケート／社長ミーティングフィードバック、分科会＝事前アンケート／事前課題／事後アンケート／アウトプット、そのほか＝事前アンケート／事前課題／事後アンケート／事後課題',
        },
        {
          master: '出欠',
          appearsIn: '出欠を取る回だけ。回答の確認と欠席申請。月例会・面談では出ない',
        },
        { master: '通知', appearsIn: 'この回のメール予約の確認・時刻変更・追加' },
        { master: '当日写真／面談記録', appearsIn: '当日の写真（面談以外）／サクセス面談の記録' },
      ],
    },
    {
      kind: 'flow',
      roles: STUDENT,
      title: '模式図: 受講生がイベントでやること',
      steps: [
        { title: '1. 一覧', body: 'ホームまたは「イベント」に自分の回だけが出る' },
        {
          title: '2. 出欠',
          body: '「出欠」タブがある回だけ、締切までに答える（月例会にはない）',
        },
        { title: '3. 事前', body: '事前の課題・アンケートがあれば期限内に提出' },
        { title: '4. 当日', body: '会場かオンラインで参加。当日その場で出す回答もある' },
        { title: '5. 事後', body: '事後の課題・アンケートがあれば提出' },
      ],
    },
    {
      kind: 'flow',
      roles: DIRECTOR,
      title: '模式図: 理事・講師が担当回でやること',
      steps: [
        { title: '1. 担当イベント', body: '自分が担当の回だけが一覧に出る' },
        { title: '2. 出席者', body: '「出欠」タブがある回では、来る人の名前を確認' },
        { title: '3. 当日写真', body: '開催のあと「当日写真」タブに写真を載せる' },
        { title: '4. 評価入力', body: '分科会などのあと、案内に従って入力' },
      ],
    },
    {
      kind: 'flow',
      roles: SUCCESS,
      title: '模式図: サクセス担当が面談まで進める流れ',
      steps: [
        { title: '1. 実施可能日', body: '自分が面談できる空き日時を登録' },
        { title: '2. 一覧', body: '担当した回（面談含む）がイベント一覧に出る' },
        { title: '3. 日時確定', body: '受講生が候補から選ぶまで「日程調整中」' },
        { title: '4. 記録', body: '終わったら「面談記録」タブに書く' },
      ],
    },
  ],

  notifications: [
    {
      kind: 'flow',
      roles: OFFICE,
      title: '模式図: お知らせメールは3階建て',
      lead: 'テンプレートとプリセットは別物です。上から下へ具体になります。',
      steps: [
        {
          title: '1. テンプレート',
          body: '「何と書くか」。件名・本文のひな型',
        },
        {
          title: '2. プリセット',
          body: '「いつ・誰に・どの文面」のルールの束（種類ごと）',
        },
        {
          title: '3. この回の予約',
          body: 'イベント作成時に行ができる。公開後、時刻が来ると送る',
        },
      ],
    },
    {
      kind: 'flow',
      roles: STUDENT,
      title: '模式図: お知らせの受け取りかた',
      steps: [
        { title: '1. 届く', body: 'メールと、このシステムの画面の両方' },
        { title: '2. 開く', body: '右上のベル、または左メニュー「システム通知」' },
        { title: '3. 進む', body: '本文の案内どおりイベントやメッセージへ' },
      ],
    },
  ],

  messages: [
    {
      kind: 'map',
      title: '模式図: メッセージは2種類だけ',
      lead: '件名はありません。全員への一斉お知らせはメッセージではなくシステム通知です。',
      rows: [
        {
          master: '事務局との1対1',
          appearsIn: '問い合わせ・個別の連絡。同じ相手とは1本の会話にまとまる',
        },
        {
          master: 'イベントのグループ',
          appearsIn: 'その回の参加者・担当者の連絡。@名前 で相手を示せる',
        },
      ],
    },
    {
      kind: 'flow',
      roles: OFFICE,
      title: '模式図: 事務局が問い合わせに答える',
      steps: [
        { title: '1. 問い合わせ対応', body: '左メニュー「メッセージ」から未読を開く' },
        { title: '2. 返信', body: '会話画面で本文を書いて送る（ファイル添付可）' },
        { title: '3. 未読が消える', body: '開いて読むと赤い丸が減る（対応件数ではない）' },
      ],
    },
  ],

  surveys: [
    {
      kind: 'flow',
      roles: OFFICE,
      title: '模式図: 課題・アンケートが届くまで',
      lead: '独立した「課題一覧」メニューはありません。必ずイベントのタブから見ます。',
      steps: [
        { title: '1. ひな型', body: '業務マスターのアンケートテンプレートで作る' },
        { title: '2. 版を使える状態に', body: '公開するまで割当の候補に出ない' },
        { title: '3. イベントに割当', body: '課題・アンケートの4タブのいずれか＋締切' },
        { title: '4. イベント公開', body: '対象の受講生に届く。回答は同じタブで確認' },
      ],
    },
    {
      kind: 'flow',
      roles: STUDENT,
      title: '模式図: 課題・アンケートに答える',
      steps: [
        { title: '1. 入口', body: 'ホームの「次にやること」、通知、またはイベント詳細' },
        { title: '2. 回答', body: '質問に答える（途中保存できるものもある）' },
        { title: '3. 提出', body: '「提出する」をクリック。締切までは直し直せることも' },
      ],
    },
  ],

  scores: [
    {
      kind: 'flow',
      roles: OFFICE,
      title: '模式図: 採点を公開するまで',
      steps: [
        { title: '1. 生データ', body: '必要ならCSVで書き出す' },
        { title: '2. 計算して取込', body: '表計算で作った採点CSVを取り込む' },
        { title: '3. 公開する', body: '確認して公開。受講生が順位を見られる' },
        {
          title: '4. 取り消し',
          body: '間違えたら公開を取り消して直前の版に戻せる',
        },
      ],
    },
    {
      kind: 'flow',
      roles: STUDENT,
      title: '模式図: 順位の見かた',
      steps: [
        { title: '1. 採点結果', body: '左メニューから開く' },
        { title: '2. 期間を選ぶ', body: '見たい月（期間）を選ぶ' },
        {
          title: '3. 内訳',
          body: '自分の行から点数の内訳も見られる（他人の内訳は不可）',
        },
      ],
    },
  ],

  success: [
    {
      kind: 'flow',
      roles: SUCCESS,
      title: '模式図: サクセス面談の一日の流れ',
      steps: [
        { title: '1. 空きを登録', body: '「実施可能日」に面談できる日時を入れる' },
        { title: '2. 予定が出る', body: '事務局が作った面談がイベント一覧に出る' },
        { title: '3. 日時確定', body: '受講生が候補から選ぶ（それまで日程調整中）' },
        { title: '4. 記録', body: '終わったら「面談記録」タブに書く' },
      ],
    },
    {
      kind: 'flow',
      roles: OFFICE,
      title: '模式図: 事務局が面談予定を立てる',
      steps: [
        { title: '1. 種別を選ぶ', body: 'イベント管理→サクセス面談（または1on1）' },
        { title: '2. 相手を選ぶ', body: '受講生1人＋担当1人。日時未定でも作れる' },
        { title: '3. 公開', body: '公開後、受講生が候補日から日時を確定する' },
      ],
    },
  ],

  masters: [
    {
      kind: 'map',
      roles: OFFICE,
      title: '模式図1: このマスターは、画面のどこに出るか',
      lead: 'マスターは「データを溜める場所」ではなく、「ほかの画面の選択肢・ルールの元」です。左がマスター、右が実際に効く場所です。',
      rows: [
        {
          master: '会員・組織（期）',
          appearsIn:
            '会員の登録・編集の「期」／会員一覧の期の列／会員CSVの期／イベント詳細「対象者」で期を選ぶとき',
        },
        {
          master: '会員・組織（コース）',
          appearsIn:
            '会員の登録・編集の「コース」（期とセットで必須）／会員一覧のコースの列／会員CSVのコース／イベント詳細「対象者」でコースを選ぶとき',
        },
        {
          master: 'イベント種別',
          appearsIn:
            'イベントの新規登録で種類を選ぶとき／左メニュー「イベント管理」の下に増える一覧／その種類のイベントに、どの自動配信ルール（プリセット）を乗せるか（標準の月例会などは最初から紐づいています）',
        },
        {
          master: 'アンケートテンプレート',
          appearsIn:
            'イベント詳細の課題・アンケートのタブでひな型を割り当てるとき。作るときに選んだイベント種別の回にだけ候補として出ます。版を公開するまで、割当の候補に出ません',
        },
        {
          master: '席次NG',
          appearsIn:
            '今は効く画面がありません（席次はシステムで組まず、出欠一覧のCSVを使って手元で作ります）。同じ卓にしない組み合わせの控えとして残す欄です',
        },
        {
          master: '通知テンプレート',
          appearsIn:
            '実際に送られるメールの件名・本文。プリセットの各ルールが「どの文面を使うか」でここを指します。招待メールの文面もここです',
        },
        {
          master: '通知プリセット',
          appearsIn:
            'イベントを作ったときに自動で並ぶ「この回のメール」。新規登録画面の見積もり、イベント詳細の「通知」タブ、左メニュー「システム通知（配信予定・履歴）」で確認します',
        },
      ],
    },
    {
      kind: 'flow',
      roles: OFFICE,
      title: '模式図2: 通知は3階建て（文面・ルール・この回の予約）',
      lead: 'テンプレートとプリセットを同じものだと思いやすいですが、役割が違います。上から下へ、1段ずつ具体になります。',
      steps: [
        {
          title: '1. 通知テンプレート',
          body: '「何と書くか」。件名と本文のひな型。イベント名などが入る穴がある。何通送るかはここでは決めない',
        },
        {
          title: '2. 通知プリセット',
          body: '「いつ・誰に・どの文面を使うか」のルールの束。イベントの種類（月例会など）に1束つく。回数分の予約はまだ無い',
        },
        {
          title: '3. 配信予定',
          body: '「この回の、この1通」。イベントを作った瞬間に、ルールの本数だけ予約行ができる。公開後、時刻が来ると送られる。この回だけ時刻変更・追加・キャンセルできる',
        },
      ],
    },
    {
      kind: 'flow',
      roles: OFFICE,
      title: '模式図3: プリセットの「1ルール」は3つの答え',
      lead: 'プリセットを開くとルールが何行も並びます。1行は「メールを1種類、自動で予約する条件」です。3つが揃って初めて1通になります。',
      steps: [
        {
          title: '誰に',
          body: '宛先。参加者、理事・講師、受講生全体、サクセス担当、事務局、全員、など',
        },
        {
          title: 'いつ',
          body: '起点（開催日時・終了日時・アンケート期限など）＋何日前／何日後＋何時。毎月○日・毎週○曜という指定もある',
        },
        {
          title: 'どの文面',
          body: '通知テンプレートを1つ選ぶ。ここで選んだ件名と本文が、送るときに使われる',
        },
      ],
    },
    {
      kind: 'example',
      roles: OFFICE,
      title: '具体例: 「開催7日前に、参加者へ案内を送る」',
      lead: '頭の中でこう組み立てます。画面の項目名も、この3つに対応しています。',
      rows: [
        {
          label: '通知テンプレート側で用意するもの',
          value:
            '名前は「月例会の開催案内」など。件名にその回のタイトルが入る穴、本文に日時が入る穴を書いて保存する',
        },
        {
          label: '通知プリセット側の1ルール',
          value:
            '宛先＝参加者／起点＝開催日時／タイミング＝7日前／配信時刻＝（例）9時／テンプレート＝今作った開催案内',
        },
        {
          label: '月例会イベントを1つ作る',
          value:
            '開催が10月10日なら、「10月3日 9時に送る」予約が1行、この回のメールに増える。同じ種類のイベントを5回作れば、同じルールから予約が5本に分かれる。新規登録画面でも、登録前にこの見積もりが出る',
        },
        {
          label: '送られる条件',
          value:
            'イベントが公開済みであること。下書きのままだと、時刻が来ても送らずスキップする。公開が予約時刻より遅れると、その通は送られない',
        },
      ],
      after:
        'プリセットを後から直しても、すでに作ったイベントの予約は自動では作り直しません。作った回だけ直すのはイベント詳細の「通知」タブです。日時未定の面談は、開催日時が無いので「開催の○日前」の予約そのものが作れません。',
    },
  ],
};

/** 模式図を必ず載せる主要トピック（D-254）。ロールごとの有無は diagramsFor で絞る */
export const HELP_DIAGRAM_REQUIRED_TOPICS = [
  'dashboard',
  'members',
  'events',
  'notifications',
  'messages',
  'surveys',
  'scores',
  'success',
  'masters',
] as const;

export function diagramsFor(topicId: string, role: Role): HelpDiagram[] {
  return (HELP_DIAGRAMS[topicId] ?? []).filter((diagram) => {
    if (!diagram.roles || diagram.roles.length === 0) return true;
    return diagram.roles.includes(role);
  });
}
